/**
 * Cart Main Item Removal Handler
 *
 * This script handles the removal of main cart items and their associated parts.
 * When a cart item with the `_is_main_item` property is removed, it also removes
 * all related items that have a `_part_of` property matching the main item's key.
 *
 * Additionally, on page load, it cleans up orphaned parts (items with `_part_of`
 * that don't have a matching main item).
 */

(function() {
  'use strict';

  // Function to get cart data
  function getCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.json());
  }

  // Function to remove multiple items from cart using /cart/update.js
  function removeItems(itemKeys) {
    if (!itemKeys || itemKeys.length === 0) {
      return Promise.resolve();
    }

    // Build updates object with all items set to quantity 0
    const updates = {};
    itemKeys.forEach(key => {
      updates[key] = 0;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        updates: updates
      })
    })
    .then(response => response.json());
  }

  // Function to handle the cart removal click
  async function handleRemovalClick(event) {
    const removeLink = event.target.closest('.cart__remove a, a.cart__remove');

    if (!removeLink) {
      return;
    }

    // Check if this is a cart change URL with quantity=0 (removal)
    const href = removeLink.getAttribute('href');
    if (!href || !href.includes('cart/change') || !href.includes('quantity=0')) {
      return;
    }

    // Extract the item key from the URL
    const urlParams = new URLSearchParams(href.split('?')[1]);
    const itemKey = urlParams.get('id');

    if (!itemKey) {
      return;
    }

    // Prevent default behavior
    event.preventDefault();

    try {
      // Get current cart data
      const cart = await getCart();

      // Find the item being removed
      const mainItem = cart.items.find(item => item.key === itemKey);

      if (!mainItem) {
        console.error('Item not found in cart:', itemKey);
        window.location.reload();
        return;
      }

      // Check if this item has _is_main_item property
      const isMainItem = mainItem.properties && mainItem.properties._is_main_item === 'true';

      let keysToRemove = [itemKey];

      if (isMainItem) {
        // Find all related items with _part_of matching this item's variant ID and _unique_id
        const mainVariantId = String(mainItem.variant_id);
        const mainUniqueId = mainItem.properties._unique_id;
        const relatedItems = cart.items.filter(item => {
          return item.properties &&
                 item.properties._part_of &&
                 String(item.properties._part_of) === mainVariantId &&
                 item.properties._unique_id === mainUniqueId;
        });

        // Add related items to removal list
        keysToRemove.push(...relatedItems.map(item => item.key));

        console.log('Removing main item (variant ID: ' + mainVariantId + ') and related items:', keysToRemove);
      }

      // Remove all items in a single request
      await removeItems(keysToRemove);

      // Reload the page to refresh the cart display
      window.location.reload();

    } catch (error) {
      console.error('Error removing items:', error);
      // Fallback: reload anyway to show updated state
      window.location.reload();
    }
  }

  // Function to clean up orphaned parts on page load
  function cleanupOrphanedParts() {
    getCart().then(cart => {
      // Build a map of main item variant IDs with their unique IDs for fast lookup
      const mainItemIdentifiers = new Map();
      cart.items.forEach(item => {
        if (item.properties && item.properties._is_main_item === 'true') {
          const key = String(item.variant_id);
          const uniqueId = item.properties._unique_id;
          if (!mainItemIdentifiers.has(key)) {
            mainItemIdentifiers.set(key, new Set());
          }
          mainItemIdentifiers.get(key).add(uniqueId);
        }
      });

      // Find orphaned parts (items with _part_of that don't match any main item variant ID + unique ID combination)
      const orphanedParts = cart.items.filter(item => {
        if (!item.properties || !item.properties._part_of) {
          return false;
        }

        const partOfVariantId = String(item.properties._part_of);
        const partUniqueId = item.properties._unique_id;

        // Check if there's a main item with matching variant ID and unique ID
        if (!mainItemIdentifiers.has(partOfVariantId)) {
          return true; // Orphaned: no main item with this variant ID
        }

        const uniqueIds = mainItemIdentifiers.get(partOfVariantId);
        return !uniqueIds.has(partUniqueId); // Orphaned: no matching unique ID
      });

      // If there are orphaned parts, remove them
      if (orphanedParts.length > 0) {
        const orphanedKeys = orphanedParts.map(item => item.key);
        console.log('Found orphaned parts to remove:', orphanedKeys);

        removeItems(orphanedKeys)
          .then(() => {
            console.log('Orphaned parts removed successfully');
            // Reload the page to show the cleaned cart
            window.location.reload();
          })
          .catch(error => {
            console.error('Error removing orphaned parts:', error);
          });
      } else {
        console.log('No orphaned parts found');
      }
    })
    .catch(error => {
      console.error('Error checking for orphaned parts:', error);
    });
  }

  // Initialize when DOM is ready
  // function init() {
  //   // Add event listener to the document to handle clicks on removal links
  //   document.addEventListener('click', handleRemovalClick);

  //   // Clean up orphaned parts on page load
  //   cleanupOrphanedParts();

  //   console.log('Cart main item removal handler initialized');
  // }
  function init() {
  // Keep removal handling available everywhere,
  // including the cart drawer.
  document.addEventListener('click', handleRemovalClick);

  // Only run the orphan cleanup immediately on the actual cart page.
  // This avoids an unnecessary /cart.js request during homepage load.
  if (document.body.classList.contains('template-cart')) {
    cleanupOrphanedParts();
  }

  // If the store uses the cart drawer, run cleanup when the
  // customer actually opens it instead of during initial page load.
  document.addEventListener('drawerOpen.CartDrawer', function() {
    cleanupOrphanedParts();
  });

  console.log('Cart main item removal handler initialized');
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
